import { uiHelper } from '@/util/ui-helper'
import { SESSION } from '@/constants/constants'

/**
 * TikTok pixel loader.
 *
 * The pixel is loaded purely so that PageView is attributed and TikTok sets its `_ttp` cookie —
 * that cookie plus the `ttclid` landing parameter are the identifiers our server-to-server events
 * are matched on. Conversions (CompleteRegistration, CompletePayment) are sent server side, from
 * feapi and beapi respectively, so nothing here calls `ttq.track()`. Adding a browser-side track
 * for those would produce a duplicate that TikTok would then have to dedup.
 *
 * Lives in one module rather than being inlined per view (as the Facebook loader is) so the two
 * call sites cannot drift apart.
 */

let initialised = false

/** Loads the TikTok base script once. Safe to call repeatedly. */
function loadTiktokBase() {
  if (window.ttq) {
    return
  }

  /* eslint-disable */
  ;(function(w, d, t) {
    w.TiktokAnalyticsObject = t
    var ttq = (w[t] = w[t] || [])
    ttq.methods = [
      'page',
      'track',
      'identify',
      'instances',
      'debug',
      'on',
      'off',
      'once',
      'ready',
      'alias',
      'group',
      'enableCookie',
      'disableCookie',
      'holdConsent',
      'revokeConsent',
      'grantConsent'
    ]
    ttq.setAndDefer = function(t, e) {
      t[e] = function() {
        t.push([e].concat(Array.prototype.slice.call(arguments, 0)))
      }
    }
    for (var i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i])
    ttq.instance = function(t) {
      for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++) ttq.setAndDefer(e, ttq.methods[n])
      return e
    }
    ttq.load = function(e, n) {
      var r = 'https://analytics.tiktok.com/i18n/pixel/events.js'
      var o = n && n.partner
      ttq._i = ttq._i || {}
      ttq._i[e] = []
      ttq._i[e]._u = r
      ttq._t = ttq._t || {}
      ttq._t[e] = +new Date()
      ttq._o = ttq._o || {}
      ttq._o[e] = n || {}
      n = d.createElement('script')
      n.type = 'text/javascript'
      n.async = !0
      n.src = r + '?sdkid=' + e + '&lib=' + t
      e = d.getElementsByTagName('script')[0]
      e.parentNode.insertBefore(n, e)
    }
  })(window, document, 'ttq')
  /* eslint-enable */
}

// The backend validates ttclid as `max:255`. Anything longer is rejected with a 422 on EVERY
// subsequent login and register, and the cookie lives for 30 days — so an over-long value in a
// shared URL locks the player out until the cookie expires. Cap on the way in, and again on the
// way out for cookies planted by an earlier build.
const TTCLID_MAX_LENGTH = 255

/**
 * Capture the click id from the landing URL. TikTok appends ?ttclid= on ad click-through only, and
 * it is gone from the URL by the time the user reaches the register form, so it is persisted here.
 *
 * 30 days matches TikTok's click attribution window; there is no point keeping it longer.
 */
export function captureTiktokClickId(query) {
  try {
    const raw = query && query.ttclid
    // `?ttclid=a&ttclid=b` arrives as an array; only a single string is meaningful.
    if (typeof raw !== 'string' || raw === '') {
      return
    }

    const ttclid = sanitizeTtclid(raw)
    if (!ttclid) {
      return
    }

    uiHelper.setCookie(SESSION.TT_CLID, ttclid, 30)
  } catch (e) {
    // This runs partway through checkReferCode(), before the affiliate-cookie write and the
    // register redirect. Never let a malformed query string abort the rest of that method.
  }
}

/**
 * Reject an unusable click id rather than truncating it: a truncated ttclid matches nobody at
 * TikTok, so silently storing half of one just looks like working attribution.
 *
 * Deliberately permissive on charset. TikTok does not document the ttclid alphabet, and rejecting
 * a valid id loses the attribution outright — the worse failure of the two. escape() already
 * neutralises the characters that could break cookie storage, and readOwnCookie() reverses it, so
 * only control characters and length actually need guarding.
 */
function sanitizeTtclid(value) {
  const ttclid = String(value)

  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001F\u007F]/.test(ttclid)) {
    return null
  }

  // setCookie() escape()s the value, which inflates length; measure what actually gets stored.
  return escape(ttclid).length > TTCLID_MAX_LENGTH ? null : ttclid
}

/**
 * Initialise every configured pixel whose domain matches the current hostname.
 *
 * @param {Array<{pixel_code: string, domain: string}>} pixelData from GET /api/tiktok/get-pixel
 */
export function initTiktokPixels(pixelData) {
  if (!Array.isArray(pixelData) || pixelData.length === 0) {
    return
  }

  // Both LayoutMaster and registerLandingPage watch the store with `immediate: true`, so this can
  // be reached several times in one session — on mount against already-loaded state, and again
  // when the component's own fetch resolves. Without this guard each pass re-runs ttq.load() and
  // ttq.page(), inflating PageView counts.
  if (initialised) {
    return
  }

  let loadedPixelCode = null

  pixelData.forEach(pixelConfig => {
    if (!pixelConfig || !pixelConfig.pixel_code || !pixelConfig.domain) {
      return
    }

    if (!hostnameMatches(pixelConfig.domain)) {
      return
    }

    loadTiktokBase()
    window.ttq.load(pixelConfig.pixel_code)

    if (!loadedPixelCode) {
      loadedPixelCode = pixelConfig.pixel_code
    }
  })

  if (loadedPixelCode) {
    initialised = true

    // One page() call covers every loaded instance.
    window.ttq.page()

    // Which pixel actually loaded, so register/login can tell the server. 30 days to match the
    // click attribution window.
    uiHelper.setCookie(SESSION.TT_PIXEL_CODE, loadedPixelCode, 30)
  }
}

/**
 * Match a configured domain against the current hostname: exact, or a subdomain of it.
 *
 * This replaced a `new RegExp()` built from the config value, which had two defects — an
 * unescaped metacharacter in a domain threw and killed every pixel after it in the list, and the
 * leading boundary group was optional, so `bhaggo.com` also matched `notbhaggo.com`.
 */
function hostnameMatches(configDomain) {
  const base = String(configDomain)
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/.*$/, '')
    .toLowerCase()

  if (!base) {
    return false
  }

  const host = String(window.location.hostname).replace(/^www\./i, '').toLowerCase()

  return host === base || host.endsWith('.' + base)
}

/**
 * The identifiers register and login send to the server. `_ttp` is set by the TikTok script, so it
 * is only present once the pixel has actually loaded.
 */
export function getTiktokIdentifiers() {
  const ttclid = readOwnCookie(SESSION.TT_CLID)

  return {
    ttPixelCode: readOwnCookie(SESSION.TT_PIXEL_CODE),
    // Re-checked on the way out: a cookie planted by an earlier build has no length cap, and an
    // over-long value 422s every login until it expires.
    ttclid: ttclid && sanitizeTtclid(ttclid),
    // NOT unescaped — TikTok's own script writes _ttp, so it was never escape()d by us.
    ttp: uiHelper.getCookie(SESSION.TT_P)
  }
}

/**
 * Read a cookie THIS module wrote. uiHelper.setCookie() escape()s the value but uiHelper.getCookie()
 * never unescapes it, so a ttclid containing `=` or `%` would otherwise reach TikTok corrupted and
 * silently match nobody. Only safe for our own cookies — values written by third-party scripts
 * were never escaped and must be read raw.
 */
function readOwnCookie(name) {
  const raw = uiHelper.getCookie(name)
  if (raw === null || typeof raw === 'undefined' || raw === '') {
    return raw
  }

  try {
    return unescape(raw)
  } catch (e) {
    return raw
  }
}
